import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminTopics = () => {
  const { token } = useAuth();
  const [topics, setTopics] = useState([]);
  const [newTopic, setNewTopic] = useState({ name: "", slug: "", description: "" });
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${API}/topics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTopics(res.data);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setNewTopic({ ...newTopic, name, slug: generateSlug(name) });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/topics`, newTopic, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewTopic({ name: "", slug: "", description: "" });
      setShowNew(false);
      fetchTopics();
      toast.success("Topic created");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create topic");
    }
  };

  const handleDelete = async (topicId) => {
    if (!window.confirm("Delete this topic?")) return;
    try {
      await axios.delete(`${API}/topics/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTopics();
      toast.success("Topic deleted");
    } catch (error) {
      toast.error("Failed to delete topic");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-topics">
        <div className="flex items-center justify-between">
          <h1 className="heading-3">Topics</h1>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-topic-btn">
            <Plus size={18} className="mr-2" />
            Add Topic
          </button>
        </div>

        {/* New Topic Form */}
        {showNew && (
          <form onSubmit={handleCreate} className="glass-card space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={newTopic.name}
                  onChange={handleNameChange}
                  required
                  className="form-input"
                  data-testid="topic-name-input"
                />
              </div>
              <div>
                <label className="form-label">Slug</label>
                <input
                  type="text"
                  value={newTopic.slug}
                  onChange={(e) => setNewTopic({ ...newTopic, slug: e.target.value })}
                  required
                  className="form-input"
                />
              </div>
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea
                value={newTopic.description}
                onChange={(e) => setNewTopic({ ...newTopic, description: e.target.value })}
                className="form-input"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="save-topic-btn">
                Create Topic
              </button>
              <button type="button" onClick={() => setShowNew(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Topics List */}
        <div className="glass-card">
          {topics.length === 0 ? (
            <p className="text-its-offwhite/60 text-center py-8">No topics yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topics.map((topic) => (
                <div
                  key={topic.id}
                  className="flex items-start justify-between p-4 rounded-lg bg-its-teal/50 border border-its-teal-light/10"
                >
                  <div>
                    <h3 className="font-medium text-white">{topic.name}</h3>
                    <p className="text-xs text-its-teal-light mb-1">/{topic.slug}</p>
                    {topic.description && (
                      <p className="text-sm text-its-offwhite/60">{topic.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(topic.id)}
                    className="p-2 text-its-offwhite/60 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTopics;
